import React, { useState, useEffect } from "react";
import axios from "axios";
import EventRSVPList from "./EventRSVPList";
import { Link } from "react-router-dom";
import { CKEditor5Editor } from "../CKEditor/CKEditor5Editor"; // Your custom CKEditor component
import { useRef } from "react"; // Ensure this is imported

const EventManager = () => {
  const [momFile, setMomFile] = useState(null);
  const [events, setEvents] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [selectedLeaderId, setSelectedLeaderId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isPastEvent, setIsPastEvent] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  

  const [formData, setFormData] = useState({
    title: "",
    shortDescription: "",
    dateTime: "",
    enddateTime: "",
    location: "",
    locationLink: "",
    virtualLink: "",
    banner: null,
    eventId: "",
  });
const maxWords = 500;
  const previousContentRef = useRef(formData.shortDescription);

  const countWords = (text) => {
    return text
      .replace(/<[^>]*>/g, "")
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  const handleCKEditorChange = (event, editor) => {
    const newValue = editor.getData();
    const wordCount = countWords(newValue);

    if (wordCount <= maxWords) {
      setFormData((prev) => ({ ...prev, shortDescription: newValue }));
      previousContentRef.current = newValue;
    } else {
      alert(`Word limit exceeded! Maximum allowed words are ${maxWords}.`);
      editor.setData(previousContentRef.current);
    }
  };
  // Fetch all events and leaders from the backend
  useEffect(() => {
    const fetchLeadersAndEvents = async () => {
      try {
        const leadersResponse = await axios.get(
          `${process.env.REACT_APP_URLVAR}/api/leaders/admin`
        );
        setLeaders(leadersResponse.data);
        const eventsResponse = await axios.get(
          `${process.env.REACT_APP_URLVAR}/api/event`,
          { headers: { "x-auth-token": localStorage.getItem("Token") } }
        );

        const enrichedEvents = eventsResponse.data.events.map((event) => {
          const leader = leadersResponse.data.find(
            (leader) => leader._id === event.leaderId
          );
          return {
            ...event,
            leaderName: leader ? leader.name : "Unknown Leader",
          };
        });

        setEvents(enrichedEvents);
      } catch (error) {
        console.error("Error fetching events or leaders:", error);
      }
    };
    fetchLeadersAndEvents();
  }, [shouldFetch]);

  const handleMomFileChange = (e) => {
    setMomFile(e.target.files[0]);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, banner: e.target.files[0] });
  };

  const handleLeaderChange = (e) => {
    setSelectedLeaderId(e.target.value);
  };

  // Prevent timezone modification
  const formatDateTimeForInput = (dateString) => {
    const date = new Date(dateString);
    const pad = (num) => String(num).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      title,
      shortDescription,
      dateTime,
      enddateTime,
      location,
      locationLink,
      virtualLink,
      eventId,
      banner,
    } = formData;

    const eventPayload = new FormData();
    eventPayload.append("title", title);
    eventPayload.append("shortDescription", shortDescription);
    eventPayload.append("dateTime", dateTime);
    eventPayload.append("enddateTime", enddateTime);
    eventPayload.append("location", location);
    eventPayload.append("locationLink", locationLink);
    eventPayload.append("virtualLink", virtualLink);
    eventPayload.append("leaderId", selectedLeaderId);
    if (banner) eventPayload.append("banner", banner);
    if (momFile) eventPayload.append("momFile", momFile);

    try {
      if (eventId) {
        // Update existing event
        await axios.put(
          `${process.env.REACT_APP_URLVAR}/api/event/${eventId}`,
          eventPayload,
          {
            headers: {
              "x-auth-token": localStorage.getItem("Token"),
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        // Create new event
        await axios.post(
          `${process.env.REACT_APP_URLVAR}/api/event`,
          eventPayload,
          {
            headers: {
              "x-auth-token": localStorage.getItem("Token"),
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      setFormData({
        title: "",
        shortDescription: "",
        dateTime: "",
        enddateTime: "",
        location: "",
        locationLink: "",
        virtualLink: "",
        banner: null,
        eventId: "",
      });
      setMomFile(null);
      setSelectedLeaderId("");
      setShouldFetch((prev) => !prev);
    } catch (error) {
      console.error("Error adding or updating event:", error);
    }
  };

  const handleDelete = async (eventId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this event?"
    );
    if (confirmDelete) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_URLVAR}/api/event/${eventId}`,
          {
            headers: { "x-auth-token": localStorage.getItem("Token") },
          }
        );
        setShouldFetch((prev) => !prev);
      } catch (error) {
        console.error("Error deleting event:", error);
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredEvents = events.filter((event) =>
    event.leaderName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (event) => {
    const isPast = new Date(event.dateTime) < new Date();
    setIsPastEvent(isPast);

    setFormData({
      title: event.title,
      shortDescription: event.shortDescription,
      dateTime: formatDateTimeForInput(event.dateTime),
      enddateTime: formatDateTimeForInput(event.enddateTime),
      location: event.location,
      locationLink: event.locationLink,
      virtualLink: event.virtualLink,
      banner: null,
      eventId: event._id,
    });

    setSelectedLeaderId(event.leaderId);
  };

  return (
    <div className="container mt-5">
      <h2 className="mb-4">Event Manager</h2>

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="mb-3">
          <label htmlFor="title" className="form-label">
            Event Title
          </label>
          <input
            type="text"
            className="form-control"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="mb-3">
          <label htmlFor="shortDescription" className="form-label">
            Short Description
          </label>
          {/* <input
            type="text"
            className="form-control"
            id="shortDescription"
            name="shortDescription"
            value={formData.shortDescription}
            onChange={handleInputChange}
            required
          /> */}

          <CKEditor5Editor
            value={formData.shortDescription}
            onChange={handleCKEditorChange}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="dateTime" className="form-label">
            Start Date & Time
          </label>
          <input
            type="datetime-local"
            className="form-control"
            id="dateTime"
            name="dateTime"
            value={formData.dateTime}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="mb-3">
          <label htmlFor="enddateTime" className="form-label">
            End Date & Time
          </label>
          <input
            type="datetime-local"
            className="form-control"
            id="enddateTime"
            name="enddateTime"
            value={formData.enddateTime}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="mb-3">
          <label htmlFor="location" className="form-label">
            Location
          </label>
          <input
            type="text"
            className="form-control"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            placeholder="Event location name or description"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="locationLink" className="form-label">
            Location Link
          </label>
          <input
            type="url"
            className="form-control"
            id="locationLink"
            name="locationLink"
            value={formData.locationLink}
            onChange={handleInputChange}
            placeholder="Link to the event's location (Google Maps, Zoom, etc.)"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="virtualLink" className="form-label">
            Virtual Link
          </label>
          <input
            type="url"
            className="form-control"
            id="virtualLink"
            name="virtualLink"
            value={formData.virtualLink}
            onChange={handleInputChange}
            placeholder="Virtual link (optional)"
          />
        </div>
        <div className="mb-3">
          <label htmlFor="banner" className="form-label">
            Banner Image
          </label>
          <input
            type="file"
            className="form-control"
            id="banner"
            name="banner"
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>
        {isPastEvent && (
          <div className="mb-3">
            <label htmlFor="momFile" className="form-label">
              Upload MOM (Minutes of Meeting) PDF
            </label>
            <input
              type="file"
              className="form-control"
              id="momFile"
              name="momFile"
              accept="application/pdf"
              onChange={handleMomFileChange}
            />
          </div>
        )}

        <div className="mb-3">
          <label htmlFor="leader" className="form-label">
            Select Leader
          </label>
          <select
            className="form-select"
            id="leader"
            value={selectedLeaderId}
            onChange={handleLeaderChange}
            required
          >
            <option value="">Select a leader</option>
            {leaders.map((leader) => (
              <option key={leader._id} value={leader._id}>
                {leader.name}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="btn btn-primary">
          {formData.eventId ? "Update Event" : "Add Event"}
        </button>
      </form>

      <div className="mt-4">
        <input
          type="text"
          className="form-control"
          placeholder="Search events by leader name"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>

      <table className="table mt-4">
        <thead>
          <tr>
            <th>Title</th>
            <th>Short Description</th>
            <th>Start Date & Time</th>
            <th>End Date & Time</th>
            <th>Location</th>
            <th>Leader</th>
            <th>RSVP</th>
            <th>Banner</th>
            <th>MOM</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredEvents.map((event) => (
            <tr key={event._id}>
              <td>{event.title}</td>
              <td>{event.shortDescription}</td>
              <td>{formatDateTimeForInput(event.dateTime)}</td>
              <td>{formatDateTimeForInput(event.enddateTime)}</td>
              <td>
                {event.location ? (
                  <a
                    href={event.locationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {event.location}
                  </a>
                ) : (
                  "Virtual"
                )}
              </td>
              <td>{event.leaderName}</td>
              <td>
                <Link
                  to={`/event/${event._id}/rsvps`}
                  className="btn btn-secondary text-white"
                >
                  RSVP
                </Link>
              </td>
              <td>
                {event.banner ? (
                  <img
                    src={`${process.env.REACT_APP_URLVAR}/${event.banner}`}
                    alt="Banner"
                    style={{ width: "100px", height: "auto" }}
                  />
                ) : (
                  "No Banner"
                )}
              </td>
              <td>
                {event.momFile ? (
                  <a
                    href={`${event.momFile}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View MOM
                  </a>
                ) : (
                  "No MOM File"
                )}
              </td>
              <td>
                <button
                  className="btn btn-secondary"
                  onClick={() => handleEdit(event)}
                >
                  <i className="bi bi-pencil-fill"></i>
                </button>
                <button
                  className="btn btn-danger ms-2"
                  onClick={() => handleDelete(event._id)}
                >
                  <i className="bi bi-trash-fill"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default EventManager;
